// Pack-first entry maths, shared by everything that fills a stock receipt line.
//
// A repackaged medicine (a 200-tablet box, a 120 ml bottle split into 20 ml ones) is entered as
// "how many packs arrived" plus "price per pack", and the dispensing-unit quantity and unit cost
// are DERIVED from those. Getting this wrong is the single most expensive bug in the module: a
// factor applied twice, or not at all, silently multiplies or divides real stock by 200.
//
// So there is exactly one implementation, here, used both by the receipt form's live pack helper
// and by the bill scanner when it pre-fills a row. If this ever changes, both move together.

/**
 * How many dispensing units one purchased pack yields, for a medicine.
 *
 * Units per pack when it is set, because someone put it there deliberately. Otherwise the
 * medicine's own geometry: a 120 ml container filled into 20 ml bottles yields six, and both
 * numbers are already on the record. Until this existed the fallback was 1, so receiving ten
 * 120 ml bottles of a decanted syrup added ten units instead of sixty and costed each at six
 * times what it really cost. Nineteen medicines were in that state and nothing flagged it,
 * because the dispensing size was read only to print a caption next to the pack inputs.
 *
 * A container that does not divide evenly returns 1, not a rounded factor. Ninety millilitres
 * filled into twenty gives four and a half bottles, and half a bottle cannot sit on a shelf; the
 * row then falls back to unit entry and the server refuses a pack-first line with a message
 * saying what to set. Rounding here would invent stock or lose it, silently.
 *
 * @param {object} medicine a medicine record, or null
 * @returns {number} the factor, at least 1
 */
export const packFactorFor = (medicine) => {
  if (!medicine) return 1;
  if (medicine.unitsPerPack > 1) return Number(medicine.unitsPerPack);
  const container = Number(medicine.packSize);
  const dispense = Number(medicine.dispenseSize);
  if (!(container > 0) || !(dispense > 0) || container <= dispense) return 1;
  const derived = container / dispense;
  // Tolerant of the float noise that 0.1-style sizes produce, strict about a genuine half.
  return Math.abs(derived - Math.round(derived)) < 1e-6 ? Math.round(derived) : 1;
};

/**
 * The factor in force for a receipt row. An edited receipt keeps the factor it was SAVED with, so
 * later changes to the medicine's pack configuration can never re-interpret an old delivery.
 */
export const rowFactorFor = (line, medicine) =>
  (line && line.unitsPerPackSnapshot) || packFactorFor(medicine);

/**
 * Derive the dispensing-unit quantity and per-unit list cost for a pack-entered row.
 *
 * Mirrors the server's own derivation (StockReceiptService.buildLines): quantity is packs x
 * factor, and the per-unit price is the pack price divided by the factor, rounded to 2dp. Note
 * that the LINE TOTAL is deliberately never computed from this rounded per-unit figure - it is
 * packs x pack price exactly, or 250/6 x 6 would come back as 250.02 instead of 250.
 *
 * Empty/invalid inputs give empty strings rather than zeros, so a half-typed row stays blank
 * instead of claiming a real value.
 *
 * @param {string|number} packsRaw     packs as typed
 * @param {string|number} packPriceRaw price per pack as typed
 * @param {number} factor              units per pack (>= 1)
 * @returns {{quantityReceived: string, unitPurchasePrice: string}} both as strings, form-ready
 */
export const derivePackRow = (packsRaw, packPriceRaw, factor) => {
  const packs = parseInt(packsRaw, 10);
  const packPrice = parseFloat(packPriceRaw);
  const safeFactor = factor > 1 ? factor : 1;
  return {
    quantityReceived: packs > 0 ? String(packs * safeFactor) : '',
    // A pack price of 0 is meaningful (free goods on a supplier bill), so the test is "is a
    // number", not "is truthy". Only a blank or unparseable price leaves the cost empty.
    unitPurchasePrice: !isNaN(packPrice) ? (packPrice / safeFactor).toFixed(2) : '',
  };
};
